import React from 'react'
import {
  CMSFinancialMetric,
  CMSOpportunityDetailsEstimatedFinancialsType,
  CMSOpportunityDetailsStrengthsType,
  CMSOpportunityDetailsType,
  CMSSectionBase,
  EstimatedFinancialChartMetricSeries,
  EstimatedFinancialChartPoint,
  EstimatedFinancialCombinedChartPoint,
  EstimatedFinancialGraphType,
  EstimatedFinancialTableRow,
  SanityColor,
} from '../../../lib/types'
import { displayDollar } from '../../../lib/utils/helpers'
import { CMSAboutCompany } from './cms-about-company'
import { CMSSourceCountries } from './cms-source-countries'
import { CMSAboutFund } from './cms-about-fund'
import { CMSRegulation } from './cms-regulation'
import { CMSFactSheet } from './cms-fact-sheet'
import { CMSProcessPoints } from './cms-process-points'
import { CMSOpportunityImages } from './cms-opportunity-images'
import { CMSRiskManagement } from './cms-risk-management'
import { CMSExecutiveSummary } from './cms-executive-summary'
import { CMSWhyShouldYouInvest } from './cms-why-should-you-invest'
import { CMSKeyPartnerShips } from './cms-partnerships'
import { CMSRiskInvolved } from './cms-risk-involved'
import { CMSAdditionalSections } from './cms-additional-sections'
import { CMSClientBase } from './cms-client-base'
import { CMSOpportunityVideos, getRenderableVideos } from './cms-opportunity-videos'
import { CMSSectionKey } from './cms-section-config'
import { CMSCompetitiveAdvantage } from './cms-competitive-advantage'

export const getTextValue = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }

    if (typeof value === 'number') {
      return `${value}`
    }
  }

  return undefined
}

export const normalizeUrl = (value: string) =>
  /^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/+/, '')}`

export const formatUrlLabel = (value: string) =>
  value
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '')

/** CMS may return a URL string or (before projection) a Sanity image reference shape. */
export const resolveCmsImageUrl = (source: unknown): string | null => {
  if (typeof source === 'string' && source.trim()) {
    return source.trim()
  }

  if (source && typeof source === 'object') {
    const asset = (source as { asset?: { url?: string } }).asset
    if (asset && typeof asset.url === 'string' && asset.url.trim()) {
      return asset.url.trim()
    }
  }

  return null
}

export const getOpportunityLogoUrl = resolveCmsImageUrl

export const getSectionDisplayOrder = (order: number | undefined) =>
  order ?? Number.MAX_SAFE_INTEGER

export type StrengthSectionEntry = {
  section: CMSOpportunityDetailsStrengthsType
  index: number
  /** Accordion DOM id suffix, e.g. `strengths` or `strengths-1` */
  idSuffix: string
  /** data-testid prefix for section shell and content */
  testIdPrefix: string
}

const buildStrengthSectionEntry = (
  section: CMSOpportunityDetailsStrengthsType,
  index: number,
): StrengthSectionEntry => ({
  section,
  index,
  idSuffix: index === 0 ? 'strengths' : `strengths-${index}`,
  testIdPrefix: index === 0 ? 'cms-strengths' : `cms-strengths-${index}`,
})

export const getStrengthSectionEntries = (
  opportunityDetails?: CMSOpportunityDetailsType,
): StrengthSectionEntry[] => {
  const strengthSections = opportunityDetails?.strengthSections?.filter(Boolean)
  if (strengthSections?.length) {
    return strengthSections.map(buildStrengthSectionEntry)
  }

  if (opportunityDetails?.strengths) {
    return [buildStrengthSectionEntry(opportunityDetails.strengths, 0)]
  }

  return []
}

export const getVisibleStrengthSectionEntries = (
  opportunityDetails?: CMSOpportunityDetailsType,
): StrengthSectionEntry[] =>
  getStrengthSectionEntries(opportunityDetails).filter((entry) => entry.section.showSection)

export const getStrengthSectionTitle = (entry: StrengthSectionEntry): string => {
  const header = entry.section.header?.trim()
  return header || 'Strengths'
}

export type SectionConfig = {
  key: CMSSectionKey
  component: React.ComponentType<any>
  propKey: string
}

export type SortedCmsSection =
  | { type: 'standard'; config: SectionConfig }
  | { type: 'strength'; entry: StrengthSectionEntry }

export const CMS_OPPORTUNITY_SECTIONS_CONFIG: SectionConfig[] = [
  { key: 'about', component: CMSAboutCompany, propKey: 'aboutCompany' },
  { key: 'executiveSummary', component: CMSExecutiveSummary, propKey: 'executiveSummary' },
  {
    key: 'competitiveAdvantage',
    component: CMSCompetitiveAdvantage,
    propKey: 'competitiveAdvantage',
  },
  {
    key: 'additionalSections',
    component: CMSAdditionalSections,
    propKey: 'additionalSections',
  },
  {
    key: 'whyShouldYouInvest',
    component: CMSWhyShouldYouInvest,
    propKey: 'whyShouldYouInvest',
  },
  { key: 'sourceCountries', component: CMSSourceCountries, propKey: 'sourceCountries' },
  { key: 'riskManagement', component: CMSRiskManagement, propKey: 'riskManagement' },
  { key: 'aboutFund', component: CMSAboutFund, propKey: 'aboutFund' },
  { key: 'regulation', component: CMSRegulation, propKey: 'regulation' },
  { key: 'factSheet', component: CMSFactSheet, propKey: 'factSheet' },
  { key: 'process', component: CMSProcessPoints, propKey: 'process' },
  { key: 'opportunityImages', component: CMSOpportunityImages, propKey: 'opportunityImages' },
  { key: 'opportunityVideos', component: CMSOpportunityVideos, propKey: 'opportunityVideos' },
  { key: 'keyPartnerships', component: CMSKeyPartnerShips, propKey: 'keyPartnerships' },
  { key: 'clientBase', component: CMSClientBase, propKey: 'clientBase' },
  { key: 'risksInvolved', component: CMSRiskInvolved, propKey: 'risksInvolved' },
]

export const getSortedCmsOpportunitySections = (
  opportunityDetails: CMSOpportunityDetailsType,
): SortedCmsSection[] => {
  const sortable: { order: number; item: SortedCmsSection }[] = []

  CMS_OPPORTUNITY_SECTIONS_CONFIG.forEach((config) => {
    const sectionData = opportunityDetails?.[config.key] as CMSSectionBase | undefined
    if (!sectionData?.showSection) {
      return
    }
    if (config.key === 'opportunityVideos') {
      if (getRenderableVideos(opportunityDetails.opportunityVideos?.videos).length === 0) {
        return
      }
    }

    sortable.push({
      order: getSectionDisplayOrder(sectionData.displayOrder),
      item: { type: 'standard', config },
    })
  })

  getVisibleStrengthSectionEntries(opportunityDetails).forEach((entry) => {
    sortable.push({
      order: getSectionDisplayOrder(entry.section.displayOrder),
      item: { type: 'strength', entry },
    })
  })

  return sortable.sort((a, b) => a.order - b.order).map(({ item }) => item)
}

const METRIC_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  revenueGrowthRate: 'Revenue Growth Rate',
  pricePerShare: 'Price per Share',
  amountRaised: 'Amount Raised',
  valuation: 'Valuation',
}

const PERCENT_METRIC_NAMES = new Set(['revenueGrowthRate', 'revenuegrowthrate'])

export const ESTIMATED_FINANCIALS_TOOLTIP =
  'Third-party estimates shown for illustration. Figures may be updated as new information becomes available.'

export const ESTIMATED_FINANCIALS_DISCLAIMER =
  'Information contained herein is based on third party estimates and is subject to update.'

export const DEFAULT_ESTIMATED_FINANCIALS_CURRENCY = 'USD'

const ISO_CURRENCY_CODE_PATTERN = /\b([A-Za-z]{3})\b/

export const resolveCurrencyUnit = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return undefined
    }

    const isoMatch = trimmed.match(ISO_CURRENCY_CODE_PATTERN)
    if (isoMatch) {
      return isoMatch[1].toUpperCase()
    }

    return trimmed.toUpperCase()
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of [
      'code',
      'value',
      'currencyUnits',
      'currencyUnit',
      'currency',
      'unit',
      'isoCode',
    ]) {
      const resolved = resolveCurrencyUnit(record[key])
      if (resolved) {
        return resolved
      }
    }
  }

  return undefined
}

export const getEstimatedFinancialsCurrencyCode = (
  metric?: Pick<CMSFinancialMetric, 'currencyUnits'> | null,
  section?: Pick<CMSOpportunityDetailsEstimatedFinancialsType, 'currencyUnits'> | null,
): string =>
  resolveCurrencyUnit(metric?.currencyUnits) ??
  resolveCurrencyUnit(section?.currencyUnits) ??
  DEFAULT_ESTIMATED_FINANCIALS_CURRENCY

export const getEstimatedFinancialDisclaimer = (
  section?: CMSOpportunityDetailsEstimatedFinancialsType,
) => section?.disclaimer?.trim() || ESTIMATED_FINANCIALS_DISCLAIMER

export const getEstimatedFinancialsSection = (
  opportunityDetails?: CMSOpportunityDetailsType,
): CMSOpportunityDetailsEstimatedFinancialsType | undefined =>
  opportunityDetails?.estimatedFinancials

export const getRenderableFinancialMetrics = (
  section?: CMSOpportunityDetailsEstimatedFinancialsType,
): CMSFinancialMetric[] =>
  (section?.financialMetrics ?? []).filter(
    (metric) =>
      metric?.metricName &&
      (metric.dataPoints?.length ?? 0) > 0 &&
      metric.dataPoints?.some((point) => point?.year && Number.isFinite(point.value)),
  )

export const formatFinancialMetricLabel = (metricName: string): string => {
  const normalized = metricName.trim()
  if (METRIC_LABELS[normalized]) {
    return METRIC_LABELS[normalized]
  }

  return normalized
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase())
}

export const getMetricChartPoints = (metric?: CMSFinancialMetric): EstimatedFinancialChartPoint[] =>
  (metric?.dataPoints ?? [])
    .filter((point) => point?.year && Number.isFinite(point.value))
    .map((point) => ({
      year: point.year,
      value: point.value,
    }))

export const withEstimatedFinancialColorOpacity = (color: string, opacity: number) => {
  const normalized = color.trim()

  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    const alpha = Math.round(Math.min(Math.max(opacity, 0), 1) * 255)
      .toString(16)
      .padStart(2, '0')
    return `${normalized}${alpha}`
  }

  return normalized
}

export const resolveSanityColor = (color?: SanityColor | string | null): string | undefined => {
  if (typeof color === 'string') {
    const trimmed = color.trim()
    if (!trimmed) {
      return undefined
    }

    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  }

  if (color?.hex?.trim()) {
    return color.hex.trim()
  }

  return undefined
}

export const resolveMetricBarColor = (
  metricColor?: SanityColor | string | null,
): string | undefined => resolveSanityColor(metricColor)

export const isPercentMetricName = (metricName: string) =>
  PERCENT_METRIC_NAMES.has(metricName.toLowerCase()) || /growth\s*rate|percent/i.test(metricName)

export const isPricePerShareMetric = (metricName: string) =>
  metricName.toLowerCase() === 'pricepershare' || metricName.toLowerCase() === 'price_per_share'

const ONE_LAKH = 100_000
const ONE_CRORE = 10_000_000

const isInrCurrency = (currencyCode?: string) => currencyCode?.trim().toUpperCase() === 'INR'

const formatInrCompactAmount = (amount: number) => {
  if (!Number.isFinite(amount) || amount === 0) {
    return '₹0'
  }

  const abs = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''

  const formatScaled = (scaled: number, suffix: string) => {
    const rounded = Math.round(scaled * 10) / 10
    const text = Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
    return `${sign}₹${text} ${suffix}`
  }

  if (abs >= ONE_CRORE) {
    return formatScaled(abs / ONE_CRORE, 'Cr')
  }

  if (abs >= ONE_LAKH) {
    return formatScaled(abs / ONE_LAKH, 'L')
  }

  return amount.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: Number.isInteger(abs) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(abs) ? 0 : 2,
  })
}

const formatCompactCurrency = (amountInDollars: number, currencyCode?: string) => {
  const resolvedCurrency = currencyCode || DEFAULT_ESTIMATED_FINANCIALS_CURRENCY
  if (isInrCurrency(resolvedCurrency)) {
    return formatInrCompactAmount(amountInDollars)
  }

  return displayDollar(`${Math.round(amountInDollars * 100)}`, resolvedCurrency, 2)
}

export const formatEstimatedFinancialAxisValue = (
  value: number,
  metricName: string,
  currencyCode?: string,
) => {
  const resolvedCurrency = currencyCode || DEFAULT_ESTIMATED_FINANCIALS_CURRENCY

  if (isPercentMetricName(metricName)) {
    return `${value}%`
  }

  if (isPricePerShareMetric(metricName)) {
    return displayDollar(`${Math.round(value * 100)}`, resolvedCurrency)
  }

  return formatCompactCurrency(value, resolvedCurrency)
}

export const formatEstimatedFinancialTooltipValue = (
  value: number,
  metricName: string,
  currencyCode?: string,
) => formatEstimatedFinancialAxisValue(value, metricName, currencyCode)

const ESTIMATED_FINANCIAL_Y_AXIS_TICK_INTERVALS = 4

const getNiceAxisStep = (roughStep: number): number => {
  if (!Number.isFinite(roughStep) || roughStep <= 0) {
    return 1
  }

  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const fraction = roughStep / magnitude
  const niceFraction = [1, 2, 5, 10].find((candidate) => candidate >= fraction) ?? 10
  return niceFraction * magnitude
}

const getEstimatedFinancialChartValues = (points: EstimatedFinancialChartPoint[]) =>
  points.flatMap((point) =>
    [point.value, point.secondaryValue].filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value),
    ),
  )

export const getEstimatedFinancialYAxisScale = (points: EstimatedFinancialChartPoint[]) => {
  const maxValue = Math.max(...getEstimatedFinancialChartValues(points), 0)
  if (maxValue <= 0) {
    return { max: 10, ticks: [0, 2, 4, 6, 8, 10] }
  }

  const step = getNiceAxisStep(maxValue / ESTIMATED_FINANCIAL_Y_AXIS_TICK_INTERVALS)
  const tickCount = Math.ceil(maxValue / step)
  const max = Number((tickCount * step).toPrecision(12))
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) =>
    Number((step * index).toPrecision(12)),
  )

  return { max, ticks }
}

export const getEstimatedFinancialYAxisMax = (points: EstimatedFinancialChartPoint[]) =>
  getEstimatedFinancialYAxisScale(points).max

export const isComparisonGraphType = (graphType: EstimatedFinancialGraphType): boolean =>
  graphType === 'bar+bar' || graphType === 'bar+line' || graphType === 'line+line'

export const getEffectiveComparisonGraphType = (
  graphType: EstimatedFinancialGraphType,
): EstimatedFinancialGraphType => {
  if (isComparisonGraphType(graphType)) {
    return graphType
  }

  return graphType === 'bar' ? 'bar+bar' : 'line+line'
}

export const getDefaultComparisonMetricName = (
  metrics: CMSFinancialMetric[],
  primaryMetricName: string,
): string => {
  const fallback = metrics.find((metric) => metric.metricName !== primaryMetricName)
  return fallback?.metricName ?? ''
}

export const buildMetricComparisonChartData = (
  primaryMetric: CMSFinancialMetric,
  compareMetric: CMSFinancialMetric,
): EstimatedFinancialChartPoint[] => {
  const years = getEstimatedFinancialMetricYears([primaryMetric, compareMetric])
  const primaryPoints = getMetricChartPoints(primaryMetric)
  const comparePoints = getMetricChartPoints(compareMetric)

  return years
    .map((year) => {
      const primaryPoint = primaryPoints.find((point) => point.year === year)
      const comparePoint = comparePoints.find((point) => point.year === year)
      const primaryValue = primaryPoint?.value ?? null
      const secondaryValue = comparePoint?.value ?? null

      if (primaryValue === null && secondaryValue === null) {
        return null
      }

      const point: EstimatedFinancialChartPoint = {
        year,
        value: primaryValue,
      }

      if (secondaryValue !== null) {
        point.secondaryValue = secondaryValue
      }

      return point
    })
    .filter((point): point is EstimatedFinancialChartPoint => point !== null)
}

export type EstimatedFinancialGraphLayerConfig = {
  bars: 0 | 1 | 2
  lines: 0 | 1 | 2
}

export const parseMetricGraphType = (graphType?: string | null): EstimatedFinancialGraphType => {
  const normalized = graphType?.trim().toLowerCase().replace(/\s+/g, '').replace(/_/g, '+') ?? ''

  switch (normalized) {
    case 'bar':
      return 'bar'
    case 'bar+bar':
      return 'bar+bar'
    case 'bar+line':
      return 'bar+line'
    case 'line+line':
      return 'line+line'
    case 'line':
    default:
      return 'line'
  }
}

export const getMetricGraphLayerConfig = (
  graphType: EstimatedFinancialGraphType,
): EstimatedFinancialGraphLayerConfig => {
  switch (graphType) {
    case 'bar':
      return { bars: 1, lines: 0 }
    case 'line':
      return { bars: 0, lines: 1 }
    case 'bar+bar':
      return { bars: 2, lines: 0 }
    case 'bar+line':
      return { bars: 1, lines: 1 }
    case 'line+line':
      return { bars: 0, lines: 2 }
    default:
      return { bars: 0, lines: 1 }
  }
}

const ESTIMATED_FINANCIALS_FALLBACK_COLORS = ['#0D86FF', '#22C55E', '#F59E0B', '#8B5CF6', '#EF4444']

export const getEstimatedFinancialMetricDataKey = (metricName: string) =>
  `metric_${metricName.replace(/[^a-zA-Z0-9]/g, '_')}`

export const getEstimatedFinancialMetricColor = (
  metric: CMSFinancialMetric,
  index: number,
): string =>
  resolveSanityColor(metric.graphColor) ??
  resolveSanityColor(metric.metricColor) ??
  ESTIMATED_FINANCIALS_FALLBACK_COLORS[index % ESTIMATED_FINANCIALS_FALLBACK_COLORS.length]

export const getEstimatedFinancialMetricYears = (metrics: CMSFinancialMetric[]): string[] => {
  const years = new Set<string>()
  metrics.forEach((metric) => {
    getMetricChartPoints(metric).forEach((point) => years.add(point.year))
  })

  return Array.from(years).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

export const buildEstimatedFinancialsTable = (
  metrics: CMSFinancialMetric[],
  section?: CMSOpportunityDetailsEstimatedFinancialsType,
): { years: string[]; rows: EstimatedFinancialTableRow[] } => {
  const years = getEstimatedFinancialMetricYears(metrics)

  const rows = metrics.map((metric) => {
    const currencyCode = getEstimatedFinancialsCurrencyCode(metric, section)
    const points = getMetricChartPoints(metric)

    return {
      metricName: metric.metricName,
      label: formatFinancialMetricLabel(metric.metricName),
      valuesByYear: Object.fromEntries(
        years.map((year) => {
          const point = points.find((entry) => entry.year === year)
          const value = point?.value
          return [
            year,
            value === undefined || value === null
              ? '—'
              : formatEstimatedFinancialTooltipValue(value, metric.metricName, currencyCode),
          ]
        }),
      ),
    }
  })

  return { years, rows }
}

export const buildEstimatedFinancialsCombinedChartData = (
  metrics: CMSFinancialMetric[],
): EstimatedFinancialCombinedChartPoint[] => {
  const years = getEstimatedFinancialMetricYears(metrics)

  return years.map((year) => {
    const row: EstimatedFinancialCombinedChartPoint = { year }

    metrics.forEach((metric) => {
      const dataKey = getEstimatedFinancialMetricDataKey(metric.metricName)
      const point = getMetricChartPoints(metric).find((entry) => entry.year === year)
      row[dataKey] = point?.value ?? null
    })

    return row
  })
}

export const buildEstimatedFinancialChartSeries = (
  metrics: CMSFinancialMetric[],
  section?: CMSOpportunityDetailsEstimatedFinancialsType,
): EstimatedFinancialChartMetricSeries[] =>
  metrics.map((metric, index) => ({
    metricName: metric.metricName,
    label: formatFinancialMetricLabel(metric.metricName),
    dataKey: getEstimatedFinancialMetricDataKey(metric.metricName),
    currencyCode: getEstimatedFinancialsCurrencyCode(metric, section),
    barColor: getEstimatedFinancialMetricColor(metric, index),
    isPercent: isPercentMetricName(metric.metricName),
  }))

export const getCombinedEstimatedFinancialYAxisMax = (
  data: EstimatedFinancialCombinedChartPoint[],
  series: EstimatedFinancialChartMetricSeries[],
) => {
  const values = data.flatMap((point) =>
    series
      .map((entry) => point[entry.dataKey])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value)),
  )

  return getEstimatedFinancialYAxisMax(values.map((value) => ({ year: '', value })))
}

export const formatEstimatedFinancialCombinedAxisValue = (
  value: number,
  series: EstimatedFinancialChartMetricSeries[],
) => {
  const referenceSeries = series.find((entry) => !entry.isPercent) ?? series[0]
  if (!referenceSeries) {
    return `${value}`
  }

  return formatEstimatedFinancialAxisValue(
    value,
    referenceSeries.metricName,
    referenceSeries.currencyCode,
  )
}

export const formatEstimatedFinancialXAxisLabel = (label: string, maxLength = 14): string => {
  const trimmed = label.trim()
  if (trimmed.length <= maxLength) {
    return trimmed
  }

  return `${trimmed.slice(0, Math.max(maxLength - 1, 1))}…`
}
